import {UIComponent} from "@webfruits/core";
import {IHotspotData} from "../../../data/IHotspotData";
import {Size} from "../../../style/Size";
import {HotspotIllustration} from "./HotspotIllustration";
import {CoreServices} from "../../../core/CoreServices";
import {gsap, Power2} from "gsap";
import {AppConfig} from "../../../config/AppConfig";
import {MathUtils} from "@webfruits/toolbox/dist/utils/MathUtils";

/******************************************************************
 * Hotspot
 *
 * @author matthias.schulz@jash.de
 *****************************************************************/

export class Hotspot extends UIComponent {

    /******************************************************************
     * Properties
     *****************************************************************/

    private _illustration: HotspotIllustration;
    private _latestMouseMoveEvent: MouseEvent;
    private _hitzone: UIComponent;

    /******************************************************************
     * Constructor
     *****************************************************************/

    constructor(private _props: IHotspotData, private _core: CoreServices) {
        super(_props.id + "-hotspot");
        this.initIllustration();
        this.initHitzone();
        this.initListeners();
        this._core.state.HOVERED_HOTSPOT.onChangeSignal.add(() => this.onHoveredHotspotChanged());
        this._core.state.SELECTED_HOTSPOT.onChangeSignal.add(() => this.onSelectedHotspotChanged());
        this._core.state.COPY_SCROLL_TOP.onChangeSignal.add(() => this.onCopyScrollChanged());
    }

    /******************************************************************
     * Public Methodes
     *****************************************************************/

    get title(): string {
        return this._props.copy.title[AppConfig.LANGUAGE_CODE];
    }

    get desc(): string {
        return this._props.copy.desc[AppConfig.LANGUAGE_CODE];
    }

    get factTitle(): string {
        if (!this._props.copy.factTitle) return "";
        return this._props.copy.factTitle[AppConfig.LANGUAGE_CODE];
    }

    get factDesc(): string {
        if (!this._props.copy.factDesc) return "";
        return this._props.copy.factDesc[AppConfig.LANGUAGE_CODE];
    }

    get factSource(): string {
        if (!this._props.copy.factSource) return "";
        return this._props.copy.factSource[AppConfig.LANGUAGE_CODE];
    }

    public playIntroAnimation() {
        gsap.set(this._illustration.view, {
            scale: this.calcScale() * 0.5,
            alpha: 0
        })
        gsap.to(this._illustration.view, {
            duration: 0.8,
            delay: Math.random() * 0.3,
            scale: this.calcScale(),
            alpha: 1,
            ease: "power4.out"
        })
        this._illustration.playHoveringAnimation()
    }

    public updateStyles() {
        this.updateIllustrationScale();
        this.applyStyle({
            position: "absolute",
            cursor: AppConfig.USE_FACT_FOR_INFO ? "default" : "pointer",
            left: this.calcLeft(),
            top: this.calcTop(),
        })
        this._illustration.applyStyle({
            width: this.calcHeight() * this._illustration.aspectRatio,
            height: this.calcHeight(),
            x: this.calcX(),
            y: this.calcY(),
            pointerEvents: "none"
        }, 1)
        this._hitzone.applyStyle({
            position: "absolute",
            width: Size.APP_WIDTH * 0.13 * this._props.scale,
            height: Size.APP_WIDTH * 0.13 * this._props.scale,
            borderRadius: 300,
            x: this.calcX(),
            y: this.calcY()
        })
    }

    /******************************************************************
     * Private Methodes
     *****************************************************************/

    private initIllustration() {
        this._illustration = new HotspotIllustration(this._props.illustrationSVG);
        this.addChild(this._illustration);
    }

    private initListeners() {
        this._hitzone.addNativeListener("mouseenter", () => this.onPointerEntered());
        this._hitzone.addNativeListener("mouseleave", () => this.onPointerLeft());
        this._hitzone.addNativeListener("click", (e: MouseEvent) => this.onPointerClicked(e));
        window.addEventListener("mousemove", (e: MouseEvent) => this.onMouseMoved(e));
        document.addEventListener("mouseleave", () => this.onMouseLeftDocument());
    }

    private initHitzone() {
        this._hitzone = new UIComponent("hit-zone");
        this.addChild(this._hitzone);
    }

    private playSelectUnselectAnimation() {
        gsap.to(this.view, {
            duration: AppConfig.HOTSPOT_SELECT_DURATION,
            top: this.calcTop(),
            scale: 1,
            alpha: 1,
            left: this.calcLeft(),
            ease: "power4.out"
        })
        gsap.to(this._illustration.transform, {
            duration: AppConfig.HOTSPOT_SELECT_DURATION,
            x: this.calcX(),
            y: this.calcY(),
            ease: "power4.out"
        })
        gsap.to(this._illustration.view, {
            duration: AppConfig.HOTSPOT_SELECT_DURATION,
            width: this.calcHeight() * this._illustration.aspectRatio,
            height: this.calcHeight(),
            ease: "power4.out"
        })
    }

    private playHideAnimation() {
        gsap.to(this.view, {
            duration: AppConfig.HOTSPOT_SELECT_DURATION,
            scale: 0.8,
            alpha: 0,
            ease: "power4.out"
        })
    }

    private updateIllustrationScale() {
        gsap.to(this._illustration.transform, {
            duration: 1.5,
            scale: this.calcScale(),
            ease: "power4.out"
        })
    }

    private updateIllustrationAlpha() {
        const scrollTop = this._core.state.COPY_SCROLL_TOP.getValue();
        const alpha = 1 - MathUtils.calcProgressWithinValues(0, 100, scrollTop);
        gsap.to(this._illustration.view, {
            duration: 1,
            alpha: alpha,
            ease: "power4.out"
        })
    }

    private calcScale(): number {
        if (this._core.state.SELECTED_HOTSPOT.hasValue()) {
            if (this._core.state.SELECTED_HOTSPOT.isValue(this)) {
                return this._props.selectedScale;
            } else {
                return AppConfig.MIN_HOTSPOT_SCALE;
            }
        }
        if (!this._latestMouseMoveEvent) return AppConfig.MIN_HOTSPOT_SCALE;
        const clientRect = this.view.getBoundingClientRect();
        const distanceX = Math.abs(clientRect.x - this._latestMouseMoveEvent.clientX);
        const distanceY = Math.abs(clientRect.y - this._latestMouseMoveEvent.clientY);
        const progressX = 1 - (distanceX / Size.APP_WIDTH);
        const progressY = 1 - (distanceY / Size.APP_HEIGHT);
        const progress = Power2.easeIn(progressX * progressY);
        return MathUtils.calcProgressWithinValues(0, 1, progress) * (AppConfig.MAX_HOTSPOT_SCALE - 1) + AppConfig.MIN_HOTSPOT_SCALE;
    }

    private calcHeight(): number {
        if (this._core.state.SELECTED_HOTSPOT.isValue(this)) {
            return Size.APP_HEIGHT * 0.2;
        }
        return Size.APP_HEIGHT * 0.15 * this._props.scale
    }

    private calcLeft(): number {
        if (this._core.state.SELECTED_HOTSPOT.isValue(this)) {
            return Size.APP_WIDTH * 0.5;
        }
        return this._props.position.x * Size.APP_WIDTH;
    }

    private calcTop(): number {
        if (this._core.state.SELECTED_HOTSPOT.isValue(this)) {
            return Size.APP_HEIGHT * 0.25;
        }
        return this._props.position.y * Size.APP_HEIGHT;
    }

    private calcX(): string {
        if (this._core.state.SELECTED_HOTSPOT.isValue(this)) {
            return "-50%"
        }
        return -this._props.position.x * 100 + "%";
    }

    private calcY(): string {
        if (this._core.state.SELECTED_HOTSPOT.isValue(this)) {
            return "-30%"
        }
        return -this._props.position.y * 100 + "%";
    }

    /******************************************************************
     * Events
     *****************************************************************/

    private onPointerClicked(e: MouseEvent) {
        e.preventDefault();
        e.stopPropagation();
        if (AppConfig.USE_FACT_FOR_INFO) {
            return;
        }
        if (this._core.state.SELECTED_HOTSPOT.isValue(this)) {
            this._core.state.SELECTED_HOTSPOT.setValue(null);
        } else {
            if (!this._core.state.SELECTED_HOTSPOT.hasValue()) {
                this._core.state.SELECTED_HOTSPOT.setValue(this);
            }
        }
    }

    private onPointerEntered() {
        this._core.state.HOVERED_HOTSPOT.setValue(this);
    }

    private onPointerLeft() {
        this._core.state.HOVERED_HOTSPOT.setValue(null);
    }

    private onMouseMoved(e: MouseEvent) {
        this._latestMouseMoveEvent = e;
        this.updateIllustrationScale();
    }

    private onMouseLeftDocument() {
        this._latestMouseMoveEvent = null;
        this.updateIllustrationScale();
    }

    private onHoveredHotspotChanged() {
        if (this._core.state.SELECTED_HOTSPOT.hasValue()) return;
        if (this._core.state.HOVERED_HOTSPOT.isValue(this)) {
            this._illustration.playEnterAnimation();
            this.applyStyle({
                zIndex: 1
            })
        } else {
            this._illustration.playLeaveAnimation();
            this.applyStyle({
                zIndex: 0
            })
        }
    }

    private onSelectedHotspotChanged() {
        this.interactive = true;
        if (this._core.state.SELECTED_HOTSPOT.hasValue()) {
            if (this._core.state.SELECTED_HOTSPOT.isValue(this)) {
                this.playSelectUnselectAnimation();
                this._illustration.playEnterAnimation();
            } else {
                this.playHideAnimation();
                this.interactive = false;
            }
        } else {
            this.playSelectUnselectAnimation();
            this._illustration.playHoveringAnimation();
        }
        this.updateIllustrationScale();
    }

    private onCopyScrollChanged() {
        this.updateIllustrationAlpha();
    }

}
